import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { NoOffersHint } from '@/lib/components/OrderForm/OrderSummary/NoOffersHint';
import { AnimatedPercentText } from '@/lib/components/Text/AnimatedPercentText';
import { AnimatedUSDText } from '@/lib/components/Text/AnimatedUSDText';
import { useMarketPrice } from '@/lib/hooks/useMarketPrice';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { Text } from '@/lib/ui-kit/text';
// For MARKET orders: no fee calculations needed (all values shown WITHOUT fees)
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { calculateSharesQuantity } from '@/lib/utils/calculateSharesQuantity';

export const PotentialPayout = () => {
  const t = useTranslations('orderForm');
  const selectedOutcomeId = useOrderFormStore.use.selectedOutcomeId();
  const intent = useOrderFormStore.use.intent();
  const action = useOrderFormStore.use.action();
  const amountUsd = useOrderFormStore.use.amountUsd();
  const livePrices = useOutcomePrices(selectedOutcomeId!);
  const orderBook = useOrderBookDataById(selectedOutcomeId ?? null);

  const yesPrice = useMarketPrice({
    outcomeId: selectedOutcomeId!,
    intent: EMarketIntent.YES,
    action: EMarketAction.BUY,
  });
  const noPrice = useMarketPrice({
    outcomeId: selectedOutcomeId!,
    intent: EMarketIntent.NO,
    action: EMarketAction.BUY,
  });

  // Check if there are offers in the order book for the selected intent and action
  // For BUY action we need asks (sellers), for SELL we need bids (buyers)
  const hasOffers = useMemo(() => {
    if (!orderBook) return false;
    if (action === EMarketAction.BUY) {
      const asks = orderBook[intent].asks;
      return asks && asks.length > 0;
    } else {
      const bids = orderBook[intent].bids;
      return bids && bids.length > 0;
    }
  }, [orderBook, intent, action]);

  const currentPrice = useMemo(() => {
    if (intent === EMarketIntent.YES) {
      return (
        (typeof livePrices?.bestAsk === 'number'
          ? livePrices.bestAsk
          : typeof livePrices?.bestBid === 'number'
            ? 1 - livePrices.bestBid
            : undefined) ?? (yesPrice ? Number(yesPrice) : 0)
      );
    } else {
      return (
        (typeof livePrices?.bestAsk === 'number'
          ? 1 - livePrices.bestAsk
          : typeof livePrices?.bestBid === 'number'
            ? livePrices.bestBid
            : undefined) ?? (noPrice ? Number(noPrice) : 0)
      );
    }
  }, [intent, livePrices, yesPrice, noPrice]);

  const { fees } = useFees();

  const potentialPayout = useMemo(() => {
    if (currentPrice <= 0 || amountUsd.lte(0) || !fees) {
      return Decimal(0);
    }
    const priceDecimal = new Decimal(currentPrice);

    // Calculate shares quantity
    // For MARKET orders: show payout WITHOUT fees
    const shares = calculateSharesQuantity(amountUsd, priceDecimal);

    return shares;
  }, [currentPrice, amountUsd, fees]);

  const potentialProfitPercent = useMemo(() => {
    if (currentPrice <= 0 || amountUsd.lte(0) || !fees) {
      return Decimal(0);
    }
    // For MARKET: all values shown WITHOUT fees
    // Percentage: (Payout - Amount) / Amount
    const priceDecimal = new Decimal(currentPrice);
    const shares = calculateSharesQuantity(amountUsd, priceDecimal);
    return shares.minus(amountUsd).div(amountUsd);
  }, [currentPrice, amountUsd, fees]);

  return (
    <div className="flex w-full items-center justify-between text-[13px] leading-4 tracking-[-0.13px]">
      <span className="flex items-center gap-1 text-v2-text-primary opacity-50">
        <span>{t('potentialPayout')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <InfoIcon />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={8}
            className="max-w-[320px] px-2 py-1.5 !text-left"
          >
            <Text variant="label-md">{t('potentialPayoutTooltip')}</Text>
          </TooltipContent>
        </Tooltip>
      </span>
      <span className={hasOffers ? 'text-v2-green' : 'text-v2-text-primary'}>
        {hasOffers ? (
          <>
            <AnimatedUSDText value={potentialPayout} alwaysShowDecimals /> (
            <AnimatedPercentText value={potentialProfitPercent} decimals={0} />)
          </>
        ) : (
          <NoOffersHint />
        )}
      </span>
    </div>
  );
};
